import {
  Box,
  Stack,
  Flex,
  Spinner,
  Badge,
} from '@chakra-ui/react';
import { FaCircle } from 'react-icons/fa';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import { useGetAgentMonitoringLogsQuery } from '../../generated/graphql';

interface AgentMonitoringPanelProps {
  agentId: string;
}

export const AgentMonitoringPanel = ({ agentId }: AgentMonitoringPanelProps) => {
  const { data, loading } = useGetAgentMonitoringLogsQuery({
    variables: { agentId, days: 7 },
    pollInterval: 60000, // Refresh every minute
  });

  const logs = data?.getAgentMonitoringLogs || [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'green.500';
      case 'warning':
        return 'yellow.500';
      case 'error':
        return 'red.500';
      default:
        return 'gray.500';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'green';
      case 'warning':
        return 'yellow';
      case 'error':
        return 'red';
      default:
        return 'gray';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Calculate health stats
  const healthCounts = logs.reduce(
    (acc, log) => {
      if (log.status === 'healthy') acc.healthy++;
      else if (log.status === 'warning') acc.warning++;
      else if (log.status === 'error') acc.error++;
      return acc;
    },
    { healthy: 0, warning: 0, error: 0 }
  );

  const latestStatus = logs.length > 0 ? logs[0].status : 'unknown';

  return (
    <Box
      bg={HelivanColor.background.primary}
      borderRadius="12px"
      border="1px solid"
      borderColor={HelivanColor.border.primary}
      p={5}
      height="full"
    >
      <Stack spacing={5}>
        <Flex justify="space-between" align="center">
          <HelivanText variant="largeSubheader">Health Monitoring</HelivanText>
          <Flex align="center" gap={2}>
            <FaCircle size={10} color={getStatusColor(latestStatus)} />
            <Badge colorScheme={getStatusBadgeColor(latestStatus)}>
              {latestStatus.toUpperCase()}
            </Badge>
          </Flex>
        </Flex>

        {/* Health summary */}
        <Box
          p={4}
          bg={HelivanColor.background.secondary}
          borderRadius="8px"
        >
          <HelivanText variant="smallBody" style={{ marginBottom: '12px' }}>
            Last 7 Days
          </HelivanText>
          <Flex gap={4}>
            <Stack spacing={0} align="center">
              <HelivanText variant="sectionHeader" colorOverride="green.500">
                {healthCounts.healthy}
              </HelivanText>
              <HelivanText variant="tinyBody" colorOverride={HelivanColor.text.blueMid}>
                Healthy
              </HelivanText>
            </Stack>
            <Stack spacing={0} align="center">
              <HelivanText variant="sectionHeader" colorOverride="yellow.500">
                {healthCounts.warning}
              </HelivanText>
              <HelivanText variant="tinyBody" colorOverride={HelivanColor.text.blueMid}>
                Warnings
              </HelivanText>
            </Stack>
            <Stack spacing={0} align="center">
              <HelivanText variant="sectionHeader" colorOverride="red.500">
                {healthCounts.error}
              </HelivanText>
              <HelivanText variant="tinyBody" colorOverride={HelivanColor.text.blueMid}>
                Errors
              </HelivanText>
            </Stack>
          </Flex>
        </Box>

        {/* Recent checks */}
        <Box>
          <HelivanText variant="smallBody" style={{ marginBottom: '12px' }}>
            Recent Health Checks
          </HelivanText>

          {loading ? (
            <Flex justify="center" py={4}>
              <Spinner size="sm" />
            </Flex>
          ) : logs.length === 0 ? (
            <Box
              p={4}
              bg={HelivanColor.background.secondary}
              borderRadius="8px"
              textAlign="center"
            >
              <HelivanText variant="smallBody" colorOverride={HelivanColor.text.blueMid}>
                No health checks recorded yet
              </HelivanText>
            </Box>
          ) : (
            <Stack spacing={2} maxHeight="300px" overflowY="auto">
              {logs.slice(0, 10).map((log) => (
                <Box
                  key={log.id}
                  p={3}
                  bg={HelivanColor.background.secondary}
                  borderRadius="8px"
                  borderLeft="3px solid"
                  borderLeftColor={getStatusColor(log.status)}
                >
                  <Flex justify="space-between" align="flex-start">
                    <Stack spacing={1}>
                      <Flex align="center" gap={2}>
                        <FaCircle size={8} color={getStatusColor(log.status)} />
                        <HelivanText variant="smallBody">
                          {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                        </HelivanText>
                      </Flex>
                      {log.errorMessage && (
                        <HelivanText
                          variant="tinyBody"
                          colorOverride="red.500"
                          style={{
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '200px',
                          }}
                        >
                          {log.errorMessage}
                        </HelivanText>
                      )}
                    </Stack>
                    <Stack spacing={0} align="flex-end">
                      <HelivanText variant="tinyBody" colorOverride={HelivanColor.text.blueMid}>
                        {formatDate(log.scanDate)}
                      </HelivanText>
                      {log.responseTime && (
                        <HelivanText variant="tinyBody" colorOverride={HelivanColor.text.blueMid}>
                          {log.responseTime}ms
                        </HelivanText>
                      )}
                    </Stack>
                  </Flex>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Stack>
    </Box>
  );
};
